import { useSelector } from 'react-redux';
import { Modal } from '@pmi/dsm-react';
import { ReactNode, useState } from 'react';
import { AdministratorUserApiModel, BaseAdmin } from '../models';
import {
  useRemoveAdminInviteMutation,
  useRemoveAdminMutation,
} from './apiSlice';
import { accountSelector } from '../../../../../shared/store/shared/accountSlice';
import { toastWithAnalytics } from '../../../../../shared/analytics';

interface RemoveAdminModalProps {
  admin: BaseAdmin;
  onCancel?: () => void;
}

export default function RemoveAdminModal({
  admin,
  onCancel = () => {},
}: RemoveAdminModalProps) {
  const { currentAccountId } = useSelector(accountSelector);
  const [removeAdmin, removedAdmin] = useRemoveAdminMutation();
  const [removeAdminInvite, removedAdminInvite] =
    useRemoveAdminInviteMutation();
  const [isProcessing, setIsProcessing] = useState(false);
  const onRemove = async () => {
    setIsProcessing(true);
    let removeAdminPromise: Promise<AdministratorUserApiModel>;
    if (admin.inviteStatus.toLowerCase() === 'validated') {
      removeAdminPromise = removeAdmin({
        accountId: currentAccountId,
        adminId: admin.id,
      }).unwrap();
    } else {
      removeAdminPromise = removeAdminInvite({
        accountId: currentAccountId,
        inviteId: admin.inviteCode,
      }).unwrap();
    }
    if (removeAdminPromise) {
      removeAdminPromise
        .then(() => {
          const successMessage = (): ReactNode => (
            <>
              You have removed{' '}
              <span data-exclude-lg>{admin.invitationEmail}</span>
            </>
          );
          toastWithAnalytics(successMessage(), 'success');
          onCancel();
          removedAdmin.reset();
          removedAdminInvite.reset();
        })
        .catch((err) => {
          const errorMessage = (): ReactNode => (
            <>
              User could not be removed{' '}
              <span data-exclude-lg>{err.data.detail}</span>
            </>
          );
          toastWithAnalytics(errorMessage(), 'danger');
          setIsProcessing(false);
        })
        .finally(() => setIsProcessing(false));
    }
  };
  const onCancelHandler = () => {
    onCancel();
  };

  if (!admin) return null;

  return (
    <Modal
      show
      onClose={onCancelHandler}
      headline="Remove?"
      description="This will remove their administrative access but they may continue to access any agreements they have been added to."
      headline_description={`Are you sure you want to remove ${admin.invitationEmail}?`}
      buttons={[
        {
          titleText: 'Cancel',
          variant: 'outline-secondary',
          onClick: onCancelHandler,
          disabled: isProcessing,
        },
        {
          titleText: 'Yes, Remove',
          variant: 'primary',
          onClick: onRemove,
          disabled: isProcessing,
        },
      ]}
    />
  );
}
